import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { BreakdownBarList } from './BreakdownBarList';
import type { StageBreakdownRow, StageCoverageSummary } from '../../types/activity-overview';

interface StageBreakdownProps {
  rows: StageBreakdownRow[];
  coverage?: StageCoverageSummary;
}

/**
 * By Communication Stage — counts only **active** stages. An activity may have
 * a stage row that is an inactive empty shell; those are excluded, so a stage
 * count below the activity total flags missing stage content. The coverage
 * summary shows how many activities have any active stages and the
 * active-stages-per-activity distribution.
 */
export function StageBreakdown({ rows, coverage }: StageBreakdownProps) {
  const total = coverage?.totalActivities;
  const hasGaps = coverage !== undefined && coverage.withoutActiveStages > 0;

  return (
    <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
      <CardHeader>
        <CardTitle className="text-heading-4 text-pine-ink">By Communication Stage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <BreakdownBarList
          primaryLabel="active stages"
          showPrimaryLabel
          rows={rows.map((row) => {
            const missing = total !== undefined ? Math.max(0, total - row.activityCount) : undefined;
            return {
              key: String(row.id),
              label: <span className="truncate">{row.title}</span>,
              primary: row.activityCount,
              secondary: missing,
              secondaryLabel: 'missing',
            };
          })}
        />

        {coverage && (
          <div className="space-y-2 pt-1">
            <p
              className={cn(
                'text-caption',
                hasGaps ? 'text-amber-800 font-medium' : 'text-pine-ink-soft'
              )}
            >
              {coverage.withActiveStages} of {coverage.totalActivities} activities have active
              stages
              {coverage.withoutActiveStages > 0 ? ` · ${coverage.withoutActiveStages} have none` : ''}
              .
            </p>

            {/* Active-stages-per-activity distribution */}
            <div className="flex items-end gap-2">
              {coverage.distribution.map((bucket) => (
                <div
                  key={bucket.activeStageCount}
                  className="flex flex-col items-center gap-0.5"
                  title={`${bucket.activityCount} activities have ${bucket.activeStageCount} active stage${
                    bucket.activeStageCount === 1 ? '' : 's'
                  }`}
                >
                  <span className="text-xs tabular-nums text-pine-ink font-medium">
                    {bucket.activityCount}
                  </span>
                  <span className="text-[10px] text-pine-ink-soft">
                    {bucket.activeStageCount} stage{bucket.activeStageCount === 1 ? '' : 's'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-caption text-pine-ink-soft">
          Only active stages count. Inactive stage rows are empty placeholder shells and are
          treated as “stage not present”. “Missing” shows how many activities lack active content
          for that stage.
        </p>
      </CardContent>
    </Card>
  );
}
